"use client";
import { useEffect, useState } from "react";
import { FiMoon, FiSun } from "react-icons/fi";

const KEY = "theme";
const MQ = "(prefers-color-scheme: dark)";

function saved(): "light" | "dark" | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

function apply(dark: boolean) {
  document.documentElement.classList.toggle("dark", dark);
  // Keep the mobile browser bar in step with the site theme, not only the OS.
  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((m) => m.setAttribute("content", dark ? "#121418" : "#fafbfc"));
}

const isDark = () => document.documentElement.classList.contains("dark");

export default function ThemeToggle({ className = "" }: { className?: string }) {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    // The <html> class is the single source of truth; every instance mirrors it.
    const sync = () => setDark(isDark());
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(root, { attributes: true, attributeFilter: ["class"] });
    // With no saved choice, follow the OS live. Applying is idempotent across instances.
    const mq = window.matchMedia(MQ);
    const onChange = (e: MediaQueryListEvent) => {
      if (!saved()) apply(e.matches);
    };
    mq.addEventListener("change", onChange);
    return () => {
      mo.disconnect();
      mq.removeEventListener("change", onChange);
    };
  }, []);

  const toggle = () => {
    const next = !isDark();
    apply(next);
    try {
      localStorage.setItem(KEY, next ? "dark" : "light");
    } catch {}
  };


  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Toggle dark mode"
      aria-pressed={dark}
      className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-ink-muted hover:text-ink transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${className}`}
    >
      <FiMoon aria-hidden="true" className="h-[18px] w-[18px] dark:hidden" />
      <FiSun aria-hidden="true" className="hidden h-[18px] w-[18px] dark:block" />
    </button>
  );
}
